import { apiCall } from '../../../services/apiUtil';

/**
 * Tennis Game API - uses game_common /games/* endpoints
 * Tennis sport_type_id = 7
 *
 * Team mapping: old team1/team2 (int 1/2) → new A/B (string)
 */

// Helper to map team number (1/2) to side ('A'/'B')
const teamToSide = (team) => (team === 1 ? 'A' : team === 2 ? 'B' : team);

export const tennisGameAPI = {
  create: async (matchData) => {
    const payload = {
      sportTypeId: 7,
      eventId: matchData.eventId,
      status: 'in_progress',
      gameName: matchData.matchName || null,
      gameSettings: {
        format: matchData.format || 'singles',
        bestOf: matchData.bestOf || 3,
        team1ServingOrder: matchData.team1ServingOrder || null,
        team2ServingOrder: matchData.team2ServingOrder || null,
        servingTeam: teamToSide(matchData.servingTeam) || 'A',
      },
      playerA: matchData.playerA || null,
      playerB: matchData.playerB || null,
    };

    return apiCall('/games', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  getById: async (id) => {
    return apiCall(`/games/${id}`);
  },

  getByEventId: async (eventId) => {
    return apiCall(`/games/event/${eventId}`);
  },

  update: async (matchId, updateData) => {
    return apiCall(`/games/${matchId}`, {
      method: 'PATCH',
      body: JSON.stringify(updateData),
    });
  },

  recordPoint: async (matchId, pointData) => {
    const payload = {
      winningTeam: teamToSide(pointData.winningTeam),
      winningPlayerId: pointData.winningPlayerId || null,
      serverPlayerId: pointData.serverPlayerId || null,
      pointType: pointData.pointType || 'point',
      errorPlayerId: pointData.errorPlayerId || null,
      isFirstServeFault: pointData.isFirstServeFault || false,
      isDoubleFault: pointData.isDoubleFault || false,
      isAce: pointData.isAce || false,
      isLet: pointData.isLet || false,
    };

    return apiCall(`/games/${matchId}/tennis/points`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  recordServeFault: async (matchId) => {
    return apiCall(`/games/${matchId}/tennis/serve-fault`, {
      method: 'POST',
    });
  },

  undoLastPoint: async (matchId) => {
    return apiCall(`/games/${matchId}/tennis/points/last`, {
      method: 'DELETE',
    });
  },

  reset: async (matchId) => {
    return apiCall(`/games/${matchId}/tennis/reset`, {
      method: 'POST',
    });
  },

  initializePlayerStats: async (matchId, players) => {
    // Map team numbers to A/B sides
    const mappedPlayers = players.map(p => ({
      ...p,
      team: teamToSide(p.team),
    }));

    return apiCall(`/games/${matchId}/tennis/player-stats`, {
      method: 'POST',
      body: JSON.stringify({ players: mappedPlayers }),
    });
  },

  setServingOrder: async (matchId, data) => {
    const payload = {};
    if (data.team1ServingOrder) payload.team1ServingOrder = data.team1ServingOrder;
    if (data.team2ServingOrder) payload.team2ServingOrder = data.team2ServingOrder;
    if (data.servingTeam) payload.servingTeam = teamToSide(data.servingTeam);

    return apiCall(`/games/${matchId}/tennis/serving-order`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    });
  },

  /**
   * Pickleball-style single-tap initial / mid-game serve selection.
   * Sets the serving team and that team's serving order (with the chosen
   * player at index 0). The other team's order can be left unset; the UI
   * shows a mid-game prompt the first time that team is about to serve.
   */
  setInitialServe: async (matchId, { servingTeam, servingPlayerId }) => {
    return apiCall(`/games/${matchId}/tennis/initial-serve`, {
      method: 'PATCH',
      body: JSON.stringify({
        servingTeam: typeof servingTeam === 'string' ? servingTeam : (servingTeam === 1 ? 'A' : 'B'),
        servingPlayerId,
      }),
    });
  },

  delete: async (matchId) => {
    return apiCall(`/games/${matchId}`, {
      method: 'DELETE',
    });
  },

  validateEventCompletion: async (eventId) => {
    return apiCall(`/games/event/${eventId}/tennis/validate-completion`);
  },

  pause: async (gameId, { reason, durationMinutes }) => {
    return apiCall(`/games/${gameId}/pause`, {
      method: 'POST',
      body: JSON.stringify({ reason, durationMinutes }),
    });
  },

  resume: async (gameId) => {
    return apiCall(`/games/${gameId}/resume`, {
      method: 'POST',
    });
  },
};

export default tennisGameAPI;
